import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { callFunction } from "../api/callFunction";
import { GoogleMap, useJsApiLoader, Marker, Circle } from "@react-google-maps/api";
import { SparklesIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../utils/googleMaps";
import { showToast } from "../utils/showToast";
import { unitForCountry, kmToUnit, unitToKm, milesToUnit, unitToMiles, roundDisplay } from "../utils/units";

// Launch-area management. Regions parameterize the idea pipeline per metro
// (geocode bounds, web-source hints, sub-area buckets, conflict radius) and gate
// which cities are open. A host can "request" a city from the app; a superAdmin
// reviews it here and flips requested → beta → live.
//
// Creating a region is the heavy part — center, catchment radius, sub-area regex
// buckets, local source domains. Two assists make it painless: an interactive
// map (drop/drag the center pin, resize the catchment circle) and an AI draft
// (city name + a point → Gemini fills the whole config, which the admin tweaks).

const STATUSES = ["requested", "beta", "live", "paused"];

// The default region is the resolver's hard-coded fallback; the backend refuses
// to delete it, so we don't offer the button for it.
const DEFAULT_REGION_ID = "sf-bay";

const STATUS_STYLES = {
  requested: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  beta: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  live: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  paused: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

// Same switch idiom as the global "Video generation" toggle in EventSuggestions.
const VideoToggle = ({ enabled, disabled, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    aria-label={label}
    title={label}
    onClick={onToggle}
    aria-busy={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      } ${disabled ? "opacity-60" : ""}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enabled ? "translate-x-5" : "translate-x-0"
        }`}
    />
  </button>
);

const EMPTY_FORM = {
  regionId: "",
  name: "",
  status: "beta",
  lat: "",
  lng: "",
  radiusKm: "80",
  conflictRadiusMiles: "5",
  timezone: "America/Los_Angeles",
  geocodeBounds: "",
  stateFilterState: "",
  stateFilterCountry: "",
  eventSourceDomains: "",
  searchTerms: "",
  subAreas: "",
  subAreaFallback: "",
};

// region doc (or AI draft) → form state. subAreas render one "Label :: pattern"
// per line; the AI draft and the stored doc share this shape (center, radiusKm,
// subAreas[], eventSourceDomains[], geocodeStateFilter), so this maps both.
function regionToForm(r) {
  return {
    regionId: r.id || "",
    name: r.name || "",
    status: r.status || "beta",
    lat: r.center?.lat ?? "",
    lng: r.center?.lng ?? "",
    radiusKm: String(r.radiusKm ?? 80),
    conflictRadiusMiles: String(r.conflictRadiusMiles ?? 5),
    timezone: r.timezone || "America/Los_Angeles",
    geocodeBounds: r.geocodeBounds || "",
    stateFilterState: r.geocodeStateFilter?.state || "",
    stateFilterCountry: r.geocodeStateFilter?.country || "",
    eventSourceDomains: (r.eventSourceDomains || []).join("\n"),
    searchTerms: (r.searchTerms || []).join("\n"),
    subAreas: (r.subAreas || []).map((s) => `${s.label} :: ${s.pattern}`).join("\n"),
    subAreaFallback: r.subAreaFallback || "",
  };
}

const linesToArray = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean);

// form state → adminUpsertRegion payload.
function formToPayload(form) {
  const subAreas = linesToArray(form.subAreas)
    .map((line) => {
      const idx = line.indexOf("::");
      if (idx === -1) return null;
      return { label: line.slice(0, idx).trim(), pattern: line.slice(idx + 2).trim() };
    })
    .filter((s) => s && s.label && s.pattern);

  const region = {
    name: form.name.trim(),
    status: form.status,
    center: { lat: Number(form.lat), lng: Number(form.lng) },
    radiusKm: Number(form.radiusKm),
    conflictRadiusMiles: Number(form.conflictRadiusMiles),
    timezone: form.timezone.trim(),
    geocodeBounds: form.geocodeBounds.trim(),
    eventSourceDomains: linesToArray(form.eventSourceDomains),
    searchTerms: linesToArray(form.searchTerms),
    subAreas,
    subAreaFallback: form.subAreaFallback.trim(),
  };
  if (form.stateFilterState.trim() && form.stateFilterCountry.trim()) {
    region.geocodeStateFilter = { state: form.stateFilterState.trim(), country: form.stateFilterCountry.trim() };
  }
  return { regionId: form.regionId || undefined, region };
}

// Numeric {lat,lng} from form strings, or null when either is blank/non-finite.
function formCenter(form) {
  const lat = Number(form.lat);
  const lng = Number(form.lng);
  if (form.lat === "" || form.lng === "" || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// Pick a zoom that frames the whole catchment circle.
function zoomForRadius(km) {
  if (km <= 10) return 11;
  if (km <= 25) return 10;
  if (km <= 50) return 9;
  if (km <= 90) return 8;
  if (km <= 160) return 7;
  if (km <= 300) return 6;
  return 5;
}

const inputCls =
  "block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: "greedy",
};

const CIRCLE_OPTIONS = {
  fillColor: "#6366f1",
  fillOpacity: 0.12,
  strokeColor: "#6366f1",
  strokeOpacity: 0.85,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: true, // radius handles — drag to resize the catchment
  zIndex: 1,
};

const DEFAULT_VIEW = { lat: 39.5, lng: -98.35, zoom: 4 }; // continental US, no pin yet

/**
 * Interactive region map. Click or drag the pin to set the center; drag the
 * circle handles to resize the catchment. center is numeric {lat,lng} or null.
 */
function RegionMap({ center, radiusKm, onPickCenter, onRadiusChange }) {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapRef = useRef(null);
  const circleRef = useRef(null);

  // Recenter / reframe whenever the center moves to a new place (city pick / AI),
  // not on every slider tick.
  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.panTo(center);
      mapRef.current.setZoom(zoomForRadius(radiusKm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng]);

  if (loadError) {
    return (
      <div className="flex h-72 w-full items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-400">
        Map failed to load.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="flex h-72 w-full items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
      </div>
    );
  }

  return (
    <div className="relative h-72 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center || DEFAULT_VIEW}
        zoom={center ? zoomForRadius(radiusKm) : DEFAULT_VIEW.zoom}
        options={MAP_OPTIONS}
        onLoad={(map) => { mapRef.current = map; }}
        onClick={(e) => onPickCenter(e.latLng.lat(), e.latLng.lng())}
      >
        {center && (
          <>
            <Marker
              position={center}
              draggable
              onDragEnd={(e) => onPickCenter(e.latLng.lat(), e.latLng.lng())}
              title="Region center — drag to move"
            />
            <Circle
              center={center}
              radius={Math.max(1, radiusKm) * 1000}
              options={CIRCLE_OPTIONS}
              onLoad={(c) => { circleRef.current = c; }}
              onRadiusChanged={() => {
                const r = circleRef.current?.getRadius();
                if (r) onRadiusChange(Math.round(r / 1000));
              }}
            />
          </>
        )}
      </GoogleMap>
      {!center && (
        <div className="pointer-events-none absolute inset-x-0 top-2 mx-auto w-fit rounded-full bg-white/90 dark:bg-gray-900/80 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 shadow-sm ring-1 ring-black/5">
          Search a city or click the map to set the center
        </div>
      )}
    </div>
  );
}

export default function Regions() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [videoBusyIds, setVideoBusyIds] = useState(() => new Set());
  const [form, setForm] = useState(null); // null = closed; object = editing/creating
  const [saving, setSaving] = useState(false);
  const [purgeSuggestions, setPurgeSuggestions] = useState(false); // danger-zone delete option

  // Modal assists.
  const [citySearch, setCitySearch] = useState("");
  const [cityResults, setCityResults] = useState([]);
  const [searchingCity, setSearchingCity] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  // Demand drill-down: null | { region, list, loading }
  const [requesters, setRequesters] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callFunction("adminListRegions", {});
      setRegions(res?.regions || []);
    } catch (err) {
      console.error("adminListRegions failed:", err);
      showToast(err.message || "Failed to load regions", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = (next) => {
    setCitySearch("");
    setCityResults([]);
    setPurgeSuggestions(false);
    setForm(next);
  };
  const closeForm = () => {
    setForm(null);
    setCitySearch("");
    setCityResults([]);
  };

  const openRequesters = async (region) => {
    setRequesters({ region, list: [], loading: true });
    try {
      const res = await callFunction("adminListRegionRequesters", { regionId: region.id });
      setRequesters({ region, list: res?.requesters || [], loading: false });
    } catch (err) {
      console.error("adminListRegionRequesters failed:", err);
      showToast(err.message || "Failed to load requesters", "error");
      setRequesters(null);
    }
  };

  // Per-region video generation. Absent field means on; only an explicit false
  // pauses the metro. The global switch on the Ideas tab stays the master.
  // Busy state is per row so one in-flight flip doesn't dead-click the others.
  const toggleRegionVideos = async (region) => {
    if (videoBusyIds.has(region.id)) return;
    const next = region.suggestionVideosEnabled === false;
    setVideoBusyIds((prev) => new Set(prev).add(region.id));
    setRegions((prev) => prev.map((r) => (r.id === region.id ? { ...r, suggestionVideosEnabled: next } : r)));
    try {
      await callFunction("adminSetRegionVideoGeneration", { regionId: region.id, enabled: next });
      showToast(next ? `Video generation on for ${region.name}` : `Video generation off for ${region.name}`);
    } catch (err) {
      console.error("adminSetRegionVideoGeneration failed:", err);
      setRegions((prev) => prev.map((r) => (r.id === region.id ? { ...r, suggestionVideosEnabled: !next } : r)));
      showToast(err.message || "Failed to update video generation", "error");
    } finally {
      setVideoBusyIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(region.id);
        return nextSet;
      });
    }
  };

  const deleteRegion = async (region, purge) => {
    const msg = purge
      ? `Delete region "${region.name}" (${region.id}) AND permanently delete its event ideas? This cannot be undone.`
      : `Delete region "${region.name}" (${region.id})? Its requesters are removed and its event ideas are left orphaned. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusyId(region.id);
    try {
      const res = await callFunction("adminDeleteRegion", { regionId: region.id, purgeSuggestions: purge });
      const deleted = res?.suggestionsDeleted || 0;
      const orphaned = res?.orphanedSuggestions || 0;
      const tail =
        deleted > 0
          ? ` · ${deleted} idea${deleted === 1 ? "" : "s"} purged`
          : orphaned > 0
          ? ` · ${orphaned} idea${orphaned === 1 ? "" : "s"} now orphaned`
          : "";
      showToast(`Deleted ${region.id}${tail}`);
      closeForm();
      await load();
    } catch (err) {
      console.error("adminDeleteRegion failed:", err);
      showToast(err.message || "Failed to delete region", "error");
    } finally {
      setBusyId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await callFunction("adminUpsertRegion", formToPayload(form));
      showToast(form.regionId ? "Region updated" : "Region created");
      closeForm();
      await load();
    } catch (err) {
      console.error("adminUpsertRegion failed:", err);
      showToast(err.message || "Failed to save region", "error");
    } finally {
      setSaving(false);
    }
  };

  const center = useMemo(() => (form ? formCenter(form) : null), [form]);
  const radiusNum = form ? Number(form.radiusKm) || 0 : 0; // km, canonical (drives the map)

  // Both radii display in one unit, chosen from the region's own country, while
  // storage stays as-is (radiusKm in km, conflictRadiusMiles in miles). Unknown
  // country → miles. Catchment is shown/edited in `unit`; conflict too.
  const unit = form ? unitForCountry(form.stateFilterCountry) : "mi";
  const radiusDisplayNum = Math.round(kmToUnit(radiusNum, unit));
  const sliderRange = unit === "km" ? { min: 5, max: 300 } : { min: 3, max: 186 };

  // Map pin moved (click / drag).
  const handlePickCenter = (lat, lng) => {
    setForm((f) => ({ ...f, lat: lat.toFixed(5), lng: lng.toFixed(5) }));
  };
  const handleRadius = (km) => {
    // Catchment is whole-km precision; round so a miles→km conversion (slider in
    // a mile region) doesn't store a long float. The mi↔km round-trip stays
    // stable at integer km for the step values we offer.
    setForm((f) => ({ ...f, radiusKm: String(Math.round(km)) }));
  };

  const handleCitySearch = async (e) => {
    e?.preventDefault?.();
    const q = citySearch.trim();
    if (q.length < 2) return;
    setSearchingCity(true);
    try {
      const res = await callFunction("geocodeCity", { query: q });
      setCityResults(res?.results || []);
      if (!(res?.results || []).length) showToast("No matches for that city", "error");
    } catch (err) {
      console.error("geocodeCity failed:", err);
      showToast(err.message || "City search failed", "error");
    } finally {
      setSearchingCity(false);
    }
  };

  const pickCity = (r) => {
    setForm((f) => ({
      ...f,
      lat: r.lat.toFixed(5),
      lng: r.lng.toFixed(5),
      name: f.name?.trim() ? f.name : (r.city || r.label || ""),
    }));
    setCityResults([]);
    setCitySearch(r.label || r.city || "");
  };

  const generateWithAI = async () => {
    const cityLabel = citySearch.trim() || form.name.trim();
    const coords = formCenter(form);
    if (!cityLabel && !coords) {
      showToast("Type a city or drop a pin first", "error");
      return;
    }
    setAiBusy(true);
    try {
      const res = await callFunction("adminSuggestRegionConfig", { cityLabel: cityLabel || undefined, coords: coords || undefined });
      const region = res?.region;
      if (!region) throw new Error("No config returned");
      // Merge the draft over the current form, preserving id + any status the
      // admin already chose. regionToForm maps the shared region shape.
      const mapped = regionToForm({ ...region, id: form.regionId, status: form.status });
      setForm(mapped);
      setCityResults([]);
      showToast(`Drafted ${region.name}`);
    } catch (err) {
      console.error("adminSuggestRegionConfig failed:", err);
      showToast(err.message || "AI generation failed", "error");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between border-b border-gray-900/10 dark:border-white/10 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Regions</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Launch areas that parameterize the idea pipeline. Only <span className="font-medium">live</span> regions
            are public; approve requested cities here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openForm({ ...EMPTY_FORM })}
          className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark"
        >
          New Region
        </button>
      </div>

      {loading ? (
        <p className="mt-8 text-gray-500 dark:text-gray-400">Loading...</p>
      ) : (
        <div className="mt-6 max-h-[70vh] overflow-auto rounded-md ring-1 ring-gray-200 dark:ring-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
              <tr>
                {["Region", "Status", "Videos", "Center", "Radius", "Demand", "Requested by"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {regions.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => openForm(regionToForm(r))}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{r.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <VideoToggle
                      enabled={r.suggestionVideosEnabled !== false}
                      disabled={videoBusyIds.has(r.id)}
                      onToggle={(e) => { e.stopPropagation(); toggleRegionVideos(r); }}
                      label={`Video generation for ${r.name}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {r.center ? `${r.center.lat?.toFixed?.(3)}, ${r.center.lng?.toFixed?.(3)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {(() => {
                      const u = unitForCountry(r.geocodeStateFilter?.country);
                      return r.radiusKm != null ? `${roundDisplay(kmToUnit(r.radiusKm, u))} ${u}` : "—";
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.requestCount > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openRequesters(r); }}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-semibold hover:bg-primary/20"
                      >
                        {r.requestCount} {r.requestCount === 1 ? "request" : "requests"}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{r.requestedBy || "—"}</td>
                </tr>
              ))}
              {regions.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No regions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="my-10 w-full max-w-4xl rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-900/10 dark:border-white/10 pb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {form.regionId ? `Edit ${form.regionId}` : "New Region"}
              </h2>
              <button type="button" onClick={closeForm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>

            {/* Assist bar: city search + AI draft */}
            <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3 ring-1 ring-gray-200 dark:ring-gray-700">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <form onSubmit={handleCitySearch} className="relative flex-1">
                  <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
                    <MapPinIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    className={`${inputCls} pl-8`}
                    placeholder="Search a city (e.g. Austin, TX)"
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                  />
                </form>
                <button
                  type="button"
                  onClick={handleCitySearch}
                  disabled={searchingCity || citySearch.trim().length < 2}
                  className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {searchingCity ? "Searching…" : "Search"}
                </button>
                <button
                  type="button"
                  onClick={generateWithAI}
                  disabled={aiBusy}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark disabled:opacity-50"
                >
                  <SparklesIcon className="h-4 w-4" />
                  {aiBusy ? "Generating…" : "Generate with AI"}
                </button>
              </div>
              {cityResults.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-y-auto rounded-md bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {cityResults.map((r, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => pickCity(r)}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {r.label || r.city}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Search drops a pin; AI drafts the whole config (radius, sub-areas, sources) from the city — review before saving.
              </p>
            </div>

            {/* Interactive map + center/radius readout */}
            <div className="mt-4">
              <RegionMap
                center={center}
                radiusKm={radiusNum}
                onPickCenter={handlePickCenter}
                onRadiusChange={handleRadius}
              />
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {center
                    ? `Center ${center.lat.toFixed(4)}, ${center.lng.toFixed(4)}`
                    : "No center set"}
                </div>
                <div className="flex items-center gap-3 sm:w-1/2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Catchment</span>
                  <input
                    type="range"
                    min={sliderRange.min}
                    max={sliderRange.max}
                    step={1}
                    value={radiusDisplayNum || (unit === "km" ? 80 : 50)}
                    onChange={(e) => handleRadius(unitToKm(Number(e.target.value), unit))}
                    className="flex-1 accent-primary"
                  />
                  <span className="w-16 text-right text-xs font-semibold text-gray-700 dark:text-gray-200">{radiusDisplayNum || 0} {unit}</span>
                </div>
              </div>
            </div>

            {/* Full config — prefilled by AI / map, editable */}
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 border-t border-gray-900/10 dark:border-white/10 pt-5">
              <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Status">
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => {
                    const active = form.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm({ ...form, status: s })}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ring-1 ring-inset transition-colors ${
                          active
                            ? "bg-primary text-white ring-primary"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Center latitude"><input className={inputCls} value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></Field>
              <Field label="Center longitude"><input className={inputCls} value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></Field>
              <Field label={`Catchment radius (${unit})`} hint="How far this region reaches; used to resolve coords → region.">
                <input
                  className={inputCls}
                  value={
                    unit === "mi"
                      ? (form.radiusKm === "" ? "" : String(roundDisplay(kmToUnit(Number(form.radiusKm), "mi"))))
                      : form.radiusKm
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({
                      ...form,
                      radiusKm: unit === "mi" ? (v === "" ? "" : String(Math.round(unitToKm(roundDisplay(Number(v)), "mi")))) : v,
                    });
                  }}
                />
              </Field>
              <Field label={`Conflict radius (${unit})`} hint="How near two events split the same crowd.">
                <input
                  className={inputCls}
                  value={
                    unit === "km"
                      ? (form.conflictRadiusMiles === "" ? "" : String(roundDisplay(milesToUnit(Number(form.conflictRadiusMiles), "km"), 1)))
                      : form.conflictRadiusMiles
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({
                      ...form,
                      conflictRadiusMiles: unit === "km" ? (v === "" ? "" : String(unitToMiles(Number(v), "km"))) : v,
                    });
                  }}
                />
              </Field>
              <Field label="Timezone (IANA)"><input className={inputCls} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
              <Field label="Geocode bounds" hint='"swLat,swLng|neLat,neLng" — leave blank to auto-derive.'><input className={inputCls} value={form.geocodeBounds} onChange={(e) => setForm({ ...form, geocodeBounds: e.target.value })} /></Field>
              <Field label="Geocode state filter (optional)" hint="e.g. CA — keeps geocoding to one state."><input className={inputCls} value={form.stateFilterState} onChange={(e) => setForm({ ...form, stateFilterState: e.target.value })} placeholder="state short name" /></Field>
              <Field label="Geocode country filter (optional)"><input className={inputCls} value={form.stateFilterCountry} onChange={(e) => setForm({ ...form, stateFilterCountry: e.target.value })} placeholder="US" /></Field>
              <div className="sm:col-span-2">
                <Field label="Event source domains (one per line)" hint="Local listing sites the web search prioritizes.">
                  <textarea rows={3} className={inputCls} value={form.eventSourceDomains} onChange={(e) => setForm({ ...form, eventSourceDomains: e.target.value })} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Search terms (one per line)">
                  <textarea rows={2} className={inputCls} value={form.searchTerms} onChange={(e) => setForm({ ...form, searchTerms: e.target.value })} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Sub-areas (one per line: Label :: regex-pattern)" hint='e.g. "Manhattan :: \\b(manhattan|soho|harlem)\\b"'>
                  <textarea rows={4} className={`${inputCls} font-mono text-xs`} value={form.subAreas} onChange={(e) => setForm({ ...form, subAreas: e.target.value })} />
                </Field>
              </div>
              <Field label="Sub-area fallback label"><input className={inputCls} value={form.subAreaFallback} onChange={(e) => setForm({ ...form, subAreaFallback: e.target.value })} /></Field>
            </div>

            {/* Danger zone — only for an existing, non-default region. */}
            {form.regionId && form.regionId !== DEFAULT_REGION_ID && (
              <div className="mt-6 rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-900/10 p-4">
                <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">Danger zone</h3>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-2">
                    <p className="text-xs text-red-600/90 dark:text-red-400/90">
                      Permanently delete this region and its requesters. This cannot be undone.
                    </p>
                    <label className="flex items-center gap-2 text-xs font-medium text-red-700 dark:text-red-300">
                      <input
                        type="checkbox"
                        checked={purgeSuggestions}
                        onChange={(e) => setPurgeSuggestions(e.target.checked)}
                        className="rounded border-red-300 dark:border-red-500/40 text-red-600 focus:ring-red-500"
                      />
                      Also delete this region's event ideas
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={busyId === form.regionId}
                    onClick={() => deleteRegion({ id: form.regionId, name: form.name }, purgeSuggestions)}
                    className="shrink-0 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {busyId === form.regionId ? "Deleting…" : "Delete region"}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-900/10 dark:border-white/10 pt-4">
              <button type="button" onClick={closeForm} className="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="button" disabled={saving} onClick={save} className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark disabled:opacity-50">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {requesters && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onClick={() => setRequesters(null)}
        >
          <div
            className="my-16 w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-900/10 dark:border-white/10 pb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Requests for {requesters.region.name}
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {requesters.region.id} · {requesters.region.requestCount || 0} total
                </p>
              </div>
              <button type="button" onClick={() => setRequesters(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>

            {requesters.loading ? (
              <p className="py-8 text-center text-sm text-gray-400">Loading…</p>
            ) : requesters.list.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No requesters recorded yet.</p>
            ) : (
              <ul className="mt-2 max-h-[60vh] divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto">
                {requesters.list.map((req) => (
                  <li key={req.uid} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                        {req.cityLabel || "Unknown city"}
                      </div>
                      <div className="truncate text-xs text-gray-400 dark:text-gray-500">
                        {req.coords ? `${req.coords.lat.toFixed(3)}, ${req.coords.lng.toFixed(3)}` : "—"} · {req.uid}
                      </div>
                    </div>
                    <div className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {req.requestedAtMs ? new Date(req.requestedAtMs).toLocaleDateString() : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
